import React from 'react';
import { useTheme } from '@rbx/ui';
import {
  getEngineRevampInlineCodeTagStyle,
  getEngineRevampInlineCodeLinkStyle,
  getEngineRevampParagraphStyle,
  getEngineRevampLinkStyle,
  getEngineRevampTableStyle,
  getEngineRevampTableRowStyle,
  getEngineRevampTableCellStyle,
  getEngineRevampTableHeaderCellStyle,
} from '@modules/ui/shared/utils/styles';
import StandardMarkdown, { StandardMarkdownProps } from './StandardMarkdown';

interface EngineMarkdownProps
  extends Omit<
    StandardMarkdownProps,
    'paragraphProps' | 'inlineCodeProps' | 'linkProps' | 'listItemProps' | 'tableProps'
  > {
  // Allow paragraph style overrides for edge cases (e.g., marginBottom: '0')
  overrideParagraphStyle?: React.CSSProperties;
}

/**
 * Markdown component with Engine Revamp styling applied.
 * Uses centralized style functions for paragraphs, inline code, links, lists, and tables.
 */
export default function EngineMarkdown({
  overrideParagraphStyle,
  children,
  ...otherProps
}: EngineMarkdownProps) {
  const theme = useTheme();

  return (
    <StandardMarkdown
      paragraphProps={{
        style: {
          ...getEngineRevampParagraphStyle(theme),
          ...overrideParagraphStyle,
        },
      }}
      inlineCodeProps={{
        inlineCodeStyle: getEngineRevampInlineCodeTagStyle(theme),
        inlineCodeLinkStyle: getEngineRevampInlineCodeLinkStyle(theme),
      }}
      linkProps={{
        linkStyle: getEngineRevampLinkStyle(theme),
      }}
      listItemProps={{
        typographyProps: {
          style: getEngineRevampParagraphStyle(theme),
        },
      }}
      tableProps={{
        tableStyle: getEngineRevampTableStyle(theme),
        tableRowStyle: getEngineRevampTableRowStyle(theme),
        tableCellStyle: getEngineRevampTableCellStyle(theme),
        tableHeaderCellStyle: getEngineRevampTableHeaderCellStyle(theme),
      }}
      {...otherProps}>
      {children}
    </StandardMarkdown>
  );
}
